import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BRAND, CATEGORIES } from "@/data/products";
import { Phone, Mail, MapPin, Instagram, Loader2, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    category: "indoor",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please share your name and phone.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you! We'll get back to you shortly.");
      setSent(true);
      setForm({
        name: "",
        phone: "",
        email: "",
        category: "indoor",
        message: "",
      });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try WhatsApp or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-cream-200/60 relative"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="text-center mb-14">
          <div className="text-[11px] tracking-[0.32em] uppercase text-gold-dark mb-4">
            — Get in Touch
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05]">
            Begin your <span className="italic gold-text">bespoke piece.</span>
          </h2>
          <p className="text-ink-soft mt-5 max-w-xl mx-auto">
            Tell us about your space, deity or design idea. We'll respond
            within 24 hours with a personalised proposal.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16">
          {/* Form */}
          <form
            onSubmit={onSubmit}
            className="bg-cream-100 border border-gold/30 p-8 md:p-10"
            data-testid="inquiry-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field
                label="Your Name *"
                value={form.name}
                onChange={onChange("name")}
                placeholder="Yogesh Varma"
                testId="input-name"
              />
              <Field
                label="Phone / WhatsApp *"
                value={form.phone}
                onChange={onChange("phone")}
                placeholder="+91 98XXXXXXXX"
                testId="input-phone"
              />
              <Field
                label="Email"
                value={form.email}
                onChange={onChange("email")}
                placeholder="you@email.com"
                type="email"
                testId="input-email"
              />
              <div className="flex flex-col">
                <label className="text-[10px] tracking-[0.28em] uppercase text-ink-soft mb-2">
                  Interested In
                </label>
                <select
                  value={form.category}
                  onChange={onChange("category")}
                  data-testid="input-category"
                  className="bg-transparent border-b border-gold/40 focus:border-gold outline-none py-2 text-ink"
                >
                  {CATEGORIES.filter((c) => c.id !== "all").map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label className="text-[10px] tracking-[0.28em] uppercase text-ink-soft mb-2 block">
                Tell us about your piece
              </label>
              <textarea
                value={form.message}
                onChange={onChange("message")}
                rows={5}
                data-testid="input-message"
                placeholder="Deity, approximate size, delivery location, timeline..."
                className="w-full bg-transparent border-b border-gold/40 focus:border-gold outline-none py-2 text-ink resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading || sent}
              data-testid="submit-inquiry-btn"
              className="mt-8 w-full flex items-center justify-center gap-3 px-8 py-4 bg-ink text-cream-100 text-[12px] tracking-[0.28em] uppercase hover:bg-gold transition-colors duration-500 disabled:opacity-70"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {sent && <Check className="w-4 h-4" />}
              {sent ? "Received — Thank You" : loading ? "Sending..." : "Send Inquiry"}
            </button>

            <p className="text-xs text-ink-soft mt-4 text-center">
              Or WhatsApp us directly at{" "}
              <a
                href={`https://wa.me/${BRAND.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                className="text-gold-dark underline"
              >
                {BRAND.phoneDisplay}
              </a>
            </p>
          </form>

          {/* Contact info */}
          <div className="space-y-5">
            <InfoCard
              icon={<Phone className="w-4 h-4 text-gold-dark" />}
              label="Studio Phone / WhatsApp"
              value={BRAND.phoneDisplay}
              href={`tel:${BRAND.phone}`}
              testId="contact-phone"
            />
            <InfoCard
              icon={<Phone className="w-4 h-4 text-gold-dark" />}
              label="Factory Phone / WhatsApp (Makrana)"
              value={BRAND.factoryPhoneDisplay}
              href={`tel:${BRAND.factoryPhone}`}
              testId="contact-factory-phone"
            />
            <InfoCard
              icon={<Mail className="w-4 h-4 text-gold-dark" />}
              label="Email"
              value={BRAND.email}
              href={`mailto:${BRAND.email}`}
              testId="contact-email"
            />
            <InfoCard
              icon={<MapPin className="w-4 h-4 text-gold-dark" />}
              label="Studio Address (Thane)"
              value={BRAND.address}
              testId="contact-address"
            />
            <InfoCard
              icon={<MapPin className="w-4 h-4 text-gold-dark" />}
              label="Factory Address (Makrana)"
              value={BRAND.factoryAddress}
              testId="contact-factory-address"
            />
            <InfoCard
              icon={<Instagram className="w-4 h-4 text-gold-dark" />}
              label="Instagram"
              value="@vardaan.arts"
              href={BRAND.instagram}
              testId="contact-instagram"
            />

            {/* Embedded map */}
            <div className="border border-gold/30 overflow-hidden">
              <iframe
                title="Vardaan Arts Location"
                src="https://www.google.com/maps?q=Ghodbandar+Road+Signal+Thane+Versova+Jain+Temple&output=embed"
                width="100%"
                height="260"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
                data-testid="contact-map"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testId }) {
  return (
    <div className="flex flex-col">
      <label className="text-[10px] tracking-[0.28em] uppercase text-ink-soft mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
        className="bg-transparent border-b border-gold/40 focus:border-gold outline-none py-2 text-ink placeholder:text-ink/30"
      />
    </div>
  );
}

function InfoCard({ icon, label, value, href, testId }) {
  const Wrapper = href ? "a" : "div";
  return (
    <Wrapper
      href={href}
      target={href && href.startsWith("http") ? "_blank" : undefined}
      rel={href && href.startsWith("http") ? "noreferrer" : undefined}
      data-testid={testId}
      className="flex items-start gap-4 p-5 bg-cream-100 border border-gold/25 hover:border-gold transition-colors block"
    >
      <div className="w-10 h-10 rounded-full border border-gold/40 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-ink-soft">
          {label}
        </div>
        <div className="text-ink mt-1 font-serif text-lg leading-snug">
          {value}
        </div>
      </div>
    </Wrapper>
  );
}
